<template>
  <div>
    <Loading
      v-if="isLoading"
      is-loading
    />
    <template v-else>
      <div class="content">
        <img
          class="image"
          :src="stateContent[state].image"
        >
        <h1 class="header">
          {{ stateContent[state].headerText }}
        </h1>
        <p
          class="body-text"
          v-html="stateContent[state].bodyText"
        />
        <strong
          v-if="stateContent[state].footerText"
          class="footer-text"
        >
          {{ stateContent[state].footerText }}
        </strong>
        <p
          v-if="stateContent[state].nextText"
          class="body-text"
        >
          {{ stateContent[state].nextText }}
        </p>
        <div class="actions">
          <button
            class="w-button w-button--blue continue-cta"
            :class="{'w-button--gray': isCTALoading}"
            :disabled="isCTALoading"
            :data-identifier="stateContent[state].dataIdentifier"
            @click="stateContent[state].ctaAction"
          >
            <span class="w-button__label">{{ stateContent[state].ctaText }}</span>
          </button>
        </div>
        <div
          class="error-message"
          v-if="errorMessage"
        >
          {{ errorMessage }}
        </div>
      </div>
    </template>
  </div>
</template>
<script>
import axios from 'axios';
import { mapState } from 'vuex';

import PageHeader from '@/components/widgets/PageHeader/PageHeader.vue';
import Loading from '@/components/widgets/Loading/Loading.vue';
import emailVerified from '@/assets/images/email-verified.png';
import emailExpired from '@/assets/images/email-expired.png';
import emailSend from '@/assets/images/email-send.png';
import { handleAPIError } from '@/utils/errorHelper';
import { buildQueryString } from '@/utils/urlHelper';

const STATE_EMAIL_VERIFIED = 'verified';
const STATE_EMAIL_ERROR = 'error';
const STATE_EMAIL_RESENT = 'resent';
const STATE_EMAIL_INITIAL_SEND = 'firstsend';
const STATE_EMAIL_ALREADY_VALIDATED = 'already-validated';

export default {
  components: {
    PageHeader,
    Loading,
  },

  data() {
    return {
      emailVerified,
      emailExpired,
      emailSend,
      isLoading: false,
      isCTALoading: false,
      errorMessage: undefined,
      state: STATE_EMAIL_ERROR,
      email: null,
      product: 'projects',
      token: '',
      tokenResponse: null,
      loginCode: null,
    };
  },

  computed: {
    ...mapState({
      isAlreadyVerified: (state) => state.site.siteInfo.installationState === 'active',
      siteURL: (state) => state.site.siteInfo.url,
    }),
    stateContent() {
      return {
        [STATE_EMAIL_VERIFIED]: {
          image: this.emailVerified,
          headerText: 'Your email address has been verified',
          bodyText: `We’ve successfully verified your email address as <strong>${this.email}</strong> <br> You’re one step closer to completing your account setup.`,
          footerText: 'Ready to continue?',
          nextText: 'Click below to move on to the next step',
          ctaText: 'Continue account setup',
          dataIdentifier: 'continue-setup',
          ctaAction: () => this.continueToApp(),
        },
        [STATE_EMAIL_ERROR]: {
          image: this.emailExpired,
          headerText: 'Email validation link has expired',
          bodyText: 'It looks like your email validation link has expired. <br> Click below to resend the verification email.',
          footerText: 'Didn’t receive the email?',
          nextText: 'Check your spam folder or click below to resend.',
          ctaText: 'Resend email',
          dataIdentifier: 'resend-email-expired',
          ctaAction: () => this.resendEmail(),
        },
        [STATE_EMAIL_ALREADY_VALIDATED]: {
          image: this.emailVerified,
          headerText: 'Email already verified',
          bodyText: 'The email address associated with this account has already been validated. <br> Click below to go to your Teamwork.com account.',
          ctaText: 'Go to your account',
          dataIdentifier: 'go-to-app',
          ctaAction: () => this.continueToOnboarding(),
        },
        [STATE_EMAIL_RESENT]: {
          image: this.emailSend,
          headerText: 'Email validation link has been resent',
          bodyText: `Your email validation link has been resent to <strong>${this.email}</strong>. <br> Click below to send another.`,
          footerText: 'Your email has been resent',
          nextText: 'Check your spam folder or click below to send another',
          ctaText: 'Resend email',
          dataIdentifier: 'resend-email-expired',
          ctaAction: () => this.resendEmail(),
        },
        [STATE_EMAIL_INITIAL_SEND]: {
          image: this.emailSend,
          headerText: 'Please verify your email address',
          bodyText: `We’ve sent a verification link to <strong>${this.email}</strong> <br> Please check your inbox and click the verification link we’ve sent to confirm your email address.`,
          footerText: 'Didn’t receive the email?',
          nextText: 'Check your spam folder or click below to resend.',
          ctaText: 'Resend email',
          dataIdentifier: 'resend-email-initial',
          ctaAction: () => this.resendEmail(),
        },
      };
    },
  },

  methods: {
    async resendEmail() {
      if (!this.email) {
        this.redirectToLogin();
      }

      this.isCTALoading = true;
      const data = {
        email: this.email,
        product: this.product,
        unvalidatedAccountLoginCode: this.loginCode,
      };

      await axios.post('/launchpad/v1/emailvalidation.json', data).then((resp) => {
        this.state = STATE_EMAIL_RESENT;
        this.errorMessage = '';
        this.email = resp.data.email;
      }).catch((err) => {
        switch (err.response.status) {
          case 401:
            this.redirectToLogin();
            break;
          case 409:
            this.state = STATE_EMAIL_ALREADY_VALIDATED;
            this.errorMessage = 'The email address associated with this account has already been validated';
            break;
          default:
            this.state = STATE_EMAIL_ERROR;
            this.errorMessage = 'Something went wrong, please try again';
            break;
        }
      }).finally(() => { this.isCTALoading = false; });
    },
    continueToApp() {
      window.location.href = this.tokenResponse.redirectURL;
    },
    continueToOnboarding() {
      window.location.href = `${this.siteURL}app/getting-started`;
    },
    redirectToLogin() {
      const params = {};
      params.continue = `/launchpad/verifyemail/${this.product}/${this.token}`;
      window.location.href = `/launchpad/login/?${buildQueryString(params)}`;
    },
  },
  async created() {
    this.isLoading = true;
    this.$store.commit('setFormState', 'email-validation');
    this.token = this.$route.params.token;
    this.product = this.$route.params.productCode;
    this.loginCode = this.$route.query.auth ? this.$route.query.auth : 'none';
    this.email = this.$route.query.email ? this.$route.query.email : null;

    if (this.isAlreadyVerified) {
      this.state = STATE_EMAIL_ALREADY_VALIDATED;
      this.isLoading = false;
      return;
    }

    if (this.token === STATE_EMAIL_INITIAL_SEND) {
      this.state = STATE_EMAIL_INITIAL_SEND;
      this.isLoading = false;
      return;
    }

    await axios.get(`/launchpad/v1/verifyemailtoken.json?token=${this.token}`).then((resp) => {
      this.tokenResponse = resp.data;
      this.state = STATE_EMAIL_VERIFIED;
      this.email = resp.data.email;
    }).catch((err) => {
      const error = handleAPIError(
        this.$store, err,
        'Something went wrong, please try again',
      );
      switch (err.response.status) {
        case 409:
          this.state = STATE_EMAIL_ALREADY_VALIDATED;
          break;
        default:
          this.state = STATE_EMAIL_ERROR;
          this.errorMessage = error.err;
          break;
      }
    }).finally(() => { this.isLoading = false; });
  },
  unmounted() {
    this.$store.commit('setFormState', '');
  },
};
</script>
<style lang="scss" scoped>
@import '../../../styles/variables/_variables.scss';

.content {
  width: 100%;
  display: flex;
  flex: auto;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center
}

.image {
  justify-content: center;
  width: 234px;
  height: 254px;
  margin-bottom: 18px;
}

.header {
  font-size: 28px;
  text-align: center;
  line-height: 40px;
  font-weight: 600;
  margin-bottom: 20px
}

.body-text {
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
}

.footer-text {
  margin-top: 24px;
}

.error-message {
  color: $color--error;
  margin-top: 12px;
}

.actions {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

.continue-cta {
  border-radius: 50px;
  width: 180px;
}
</style>



// WEBPACK FOOTER //
// EmailValidation.vue?02319db5